import { useCallback, useContext } from 'react';
import { UNSAFE_DataRouterContext, useLocation, useSearchParams } from 'react-router-dom';

export type ListParamChanges = Readonly<Record<string, string | ReadonlyArray<string> | null>>;

export type ListParams = {
  readonly params: URLSearchParams;
  readonly setParams: (changes: ListParamChanges, options?: { readonly replace?: boolean }) => void;
};

export const SEARCH_PARAM = 'q';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useListParams(): ListParams {
  const [params, setSearchParams] = useSearchParams();
  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const { pathname } = useLocation();

  const setParams = useCallback(
    (changes: ListParamChanges, { replace = false }: { readonly replace?: boolean } = {}) => {
      setSearchParams(
        (prev) => {
          // `prev` is the address this component last rendered. A change made while an earlier one is
          // still loading, or has landed but not yet re-rendered (a sort clicked, then a filter straight
          // after), builds on the router's newest address read at call time, or it drops the earlier change.
          const latest = router === undefined ? null : (router.state.navigation.location ?? router.state.location);
          const next = new URLSearchParams(latest?.pathname === pathname ? latest.search : prev);

          for (const [key, value] of Object.entries(changes)) {
            next.delete(key);

            if (typeof value === 'string') {
              if (value !== '') next.set(key, value);

              continue;
            }

            if (value === null) continue;

            for (const item of value) next.append(key, item);
          }

          return next;
        },
        { replace },
      );
    },
    [setSearchParams, router, pathname],
  );

  return { params, setParams };
}

export function readEnumParam<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: ReadonlyArray<T>,
): T | null {
  const value = params.get(key);

  return allowed.find((candidate) => candidate === value) ?? null;
}

export function readEnumParams<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: ReadonlyArray<T>,
): ReadonlyArray<T> {
  const values = params.getAll(key);

  return allowed.filter((candidate) => values.includes(candidate));
}

export function readUuidParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);

  return value !== null && UUID_RE.test(value) ? value : null;
}

export function readDateParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);

  return value !== null && !Number.isNaN(Date.parse(value)) ? value : null;
}

export function readUuidParams(params: URLSearchParams, key: string): ReadonlyArray<string> {
  return [...new Set(params.getAll(key).filter((value) => UUID_RE.test(value)))];
}
